<script lang="ts">
    import type { Readable, Writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { isEqual } from 'lodash-es'
    import { toUnicode } from 'punycode-esm'

    import { gettext as _, resolve } from 'hydria/services/gettext'

    import { subaccount } from 'wdom/subaccount'
    import { keyTexts, typeKeys } from 'wdom/zone/zone'
    import type { EditableRecord, Zone, ZoneTemplate } from 'wdom/zone/zone'

    interface Props {
        rg: EditableRecord
        status: Writable<'loading' | 'ready' | 'not-found' | 'error'>
        // export let records: Writable<EditableRecord[]>
        busy: Writable<boolean | string>
        zoneUnicode: Readable<string | undefined>
        zoneEdit: Writable<Zone | ZoneTemplate | undefined>
        removeRecord: (rg: EditableRecord) => void
        editRecord: () => void
        recordsChanged: () => void
    }

    let { rg = $bindable(), status, busy, zoneUnicode, zoneEdit, removeRecord, editRecord, recordsChanged }: Props = $props()

    let localTypeKeys = $derived(typeKeys[typeKeys[rg.edited.type] != null ? rg.edited.type : 'default'])
    let gClass = $derived.by(() => {
        switch (rg.status) {
            case 'remove':
                return 'bg-danger'
            case 'new':
                return 'bg-success'
            case 'changed':
                return 'bg-warning'
        }
        return ''
    })

    function edit() {
        editRecord()
    }
    function undo() {
        if (rg.status == 'new') {
            removeRecord(rg)
        } else {
            rg.edited = { ...rg.saved }
            rg.status = 'saved'
            recordsChanged()
        }
    }
    function remove() {
        if (rg.status == 'new') {
            removeRecord(rg)
        } else if (rg.status == 'remove') {
            rg.status = isEqual(rg.saved, rg.edited) ? 'saved' : 'changed'
            recordsChanged()
        } else {
            rg.status = 'remove'
            recordsChanged()
        }
    }
</script>

<style>
    .rr.bg-success,
    .rr.bg-warning,
    .rr.bg-danger {
        --bs-bg-opacity: 0.2;
    }
    .rr-cell {
        /* Ne pas convertir par exemple '2x2' en '2×2' */
        font-feature-settings: 'calt' 0;
    }
    .rr-cell:nth-child(2) {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-feature-settings: 'case' 1;
    }
    .rr-cell:nth-child(3) {
        font-feature-settings: 'tnum' 1;
    }

    .rr-cell:nth-child(5) {
        font-feature-settings:
            'tnum' 1,
            'case' 1;
    }
    .rr-cell:nth-child(5),
    .rr-cell:nth-child(5) * {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .np-single .rrv-label {
        display: none;
    }

    /* Pour afficher avec un label par dessus */
    .np-multi {
        padding-top: 0;
    }
    .np-multi > div {
        display: flex;
    }
    .np-multi > div > span:not(:last-child) {
        flex: 0 0 auto;
        margin-right: 2ex;
    }
    .np-multi > div > span:last-child {
        flex: 1 0 0;
    }
    .np-multi > div > span {
        display: grid;
        grid-template-rows: auto 1fr;
        grid-template-columns: auto;
    }
    .np-multi > div > span .rrv-label {
        line-height: 12px;
        font-size: 0.6em;

        font-weight: 500;
        text-transform: uppercase;

        /* text-transform: lowercase;
           font-variant: small-caps; */
    }

    /* Pour afficher en "lignes"
    &.np-multi {
        > div > span {
            display: block;
            .rrv-label {
                margin-right: 1ex;
                &::after {
                    html[lang='fr'] & {
                        padding-left: 0.25ex;
                    }
                    content: ':';
                }
            }
        }
    } */
</style>

{#if $status == 'ready' && $zoneEdit != null}
    <div class="rr {gClass}">
        <div class="rr-cell">
            {#if rg.hasError}<Icon inline icon="fa:exclamation-triangle" />{/if}
        </div>
        <div class="rr-cell">
            <span class:modified={rg.status == 'changed' && rg.edited.idna != rg.saved.idna}>
                {#if rg.edited.idna != ''}<span>{toUnicode(rg.edited.idna)}</span><span class="text-muted">.</span>{/if}<span class="text-muted">{$zoneUnicode}</span>
            </span>
        </div>
        <div class="rr-cell"><span class:text-muted={rg.edited.ttl == null} class:modified={rg.status == 'changed' && rg.edited.ttl !== rg.saved.ttl}>{rg.edited.ttl != null ? rg.edited.ttl : $zoneEdit['default_ttl'] != null ? $zoneEdit['default_ttl'] : $_('default')}</span></div>
        <div class="rr-cell"><span class:modified={rg.status == 'changed' && rg.edited.type != rg.saved.type}>{rg.edited.type}</span></div>
        <div class="rr-cell np-{localTypeKeys.length}" class:np-single={localTypeKeys.length == 1} class:np-multi={localTypeKeys.length >= 2}>
            <div>
                {#each localTypeKeys as key}
                    <span><span class="rrv-label">{$resolve(keyTexts[key])}</span><span class="rrv-value" class:modified={rg.status == 'changed' && rg.edited[key] !== rg.saved[key]}>{rg.edited[key]}</span></span>
                {/each}
            </div>
        </div>
        <div class="rr-cell">
            {#if $subaccount.zone}
                <div class="btn-group btn-group-sm">
                    <button type="button" class="btn btn-outline-secondary" onclick={() => edit()} disabled={!!$busy}><Icon inline icon="fa:pencil" /></button>
                    <button type="button" class="btn btn-outline-secondary" disabled={rg.status == 'saved' || rg.status == 'new' || !!$busy} onclick={() => undo()}><Icon inline icon="fa:undo" /></button>
                    <button type="button" class="btn btn-outline-secondary" class:active={rg.status == 'remove'} onclick={() => remove()} disabled={!!$busy}><Icon inline icon="fa:trash" /></button>
                </div>
            {/if}
        </div>
    </div>
{/if}
